import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TeamUser } from '@callhub/shared';
import { api } from '@/lib/api';

function formatLastActivity(value: string | null): string {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No activity yet';
  return date.toLocaleString();
}

export function AgentsPage() {
  const usersQuery = useQuery({
    queryKey: ['team-users'],
    queryFn: async () => {
      const res = await api.getUsers();
      return res.data;
    },
    refetchInterval: 10_000,
  });

  const rankedUsers = useMemo(() => {
    const users = usersQuery.data ?? [];
    return [...users].sort((a, b) => {
      const scoreA = a.messageCount + a.callCount * 2;
      const scoreB = b.messageCount + b.callCount * 2;
      if (scoreB !== scoreA) return scoreB - scoreA;

      const aTs = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
      const bTs = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
      return bTs - aTs;
    });
  }, [usersQuery.data]);

  const summary = useMemo(() => {
    const users = usersQuery.data ?? [];
    const totals = users.reduce(
      (acc, user) => {
        acc.messages += user.messageCount;
        acc.calls += user.callCount;
        if (user.manualVoiceAvailability) acc.manualAvailability += 1;
        if (user.voiceRegistered) acc.registered += 1;
        if (user.voiceEligible) acc.eligible += 1;
        return acc;
      },
      { messages: 0, calls: 0, manualAvailability: 0, registered: 0, eligible: 0 },
    );

    return {
      totalAgents: users.length,
      activeAgents: users.filter((user) => user.messageCount > 0 || user.callCount > 0).length,
      outboundMessages: totals.messages,
      callsPlaced: totals.calls,
      manualAvailability: totals.manualAvailability,
      registeredDevices: totals.registered,
      voiceEligible: totals.eligible,
    };
  }, [usersQuery.data]);

  return (
    <>
      <header className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground">Agents</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Clerk-backed team users with outbound call and SMS ownership tracking.
        </p>
      </header>

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Total Agents</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{summary.totalAgents}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Active Agents</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{summary.activeAgents}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Outbound SMS</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{summary.outboundMessages}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Calls Placed</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{summary.callsPlaced}</p>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Manual Availability On</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {summary.manualAvailability}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Registered Voice Devices</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{summary.registeredDevices}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <span className="text-sm text-muted-foreground">Inbound Voice Eligible</span>
          <p className="mt-2 text-2xl font-semibold text-foreground">{summary.voiceEligible}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <p className="mb-4 text-sm text-muted-foreground">Agent Activity</p>

        {usersQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading users…</p>
        ) : usersQuery.isError ? (
          <p className="text-sm text-red-500">{(usersQuery.error as Error).message}</p>
        ) : rankedUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No team users yet. Once a Clerk user signs in, they will appear here.
          </p>
        ) : (
          <div className="space-y-3">
            {rankedUsers.map((user: TeamUser) => {
              const score = user.messageCount + user.callCount * 2;
              return (
                <div
                  key={user.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-background p-4"
                >
                  <div className="min-w-[14rem]">
                    <p className="font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    <p className="mt-1 text-xs uppercase text-muted-foreground">{user.role}</p>
                    <div className="mt-2 flex flex-wrap gap-1 text-[11px]">
                      <span
                        className={`rounded-full px-2 py-0.5 ${
                          user.manualVoiceAvailability
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        Manual {user.manualVoiceAvailability ? 'on' : 'off'}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 ${
                          user.voiceRegistered
                            ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        Device {user.voiceRegistered ? 'registered' : 'offline'}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 ${
                          user.voiceEligible
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {user.voiceEligible ? 'Inbound eligible' : 'Not eligible'}
                      </span>
                    </div>
                  </div>

                  <div className="grid min-w-[16rem] grid-cols-3 gap-4 text-right">
                    <div>
                      <p className="text-xs text-muted-foreground">SMS</p>
                      <p className="text-sm font-semibold text-foreground">{user.messageCount}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Calls</p>
                      <p className="text-sm font-semibold text-foreground">{user.callCount}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Score</p>
                      <p className="text-sm font-semibold text-foreground">{score}</p>
                    </div>
                  </div>

                  <div className="min-w-[13rem] text-right">
                    <p className="text-xs text-muted-foreground">Last activity</p>
                    <p className="text-xs text-foreground">
                      {formatLastActivity(user.lastActivityAt)}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Voice heartbeat:{' '}
                      {user.voiceLastHeartbeatAt
                        ? new Date(user.voiceLastHeartbeatAt).toLocaleTimeString()
                        : 'none'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
